import apiClient, { apiAuthClient } from 'services/api/apiService';
import { getUrlWithQueryParams } from './api/getUrlWithQueryParams';
import { SortByOption } from 'pages/NewOrdersFlowPages/types';
import { IEditBuyerDTO } from 'pages/BuyersSinglePage/hooks';
import { IEditCompanyDataDTO } from 'pages/CompanyManagementPage/types';
import { SortDirection } from 'components/Table/types';
import { AppMode } from 'store/Common/reducers/common';
import { IRegisterNewCompanyParams } from 'types/Company.types';
import { ITogglePublicModelDTO } from 'pages/MyModelsSinglePage/hooks';
import { ShippingType } from 'types/Order.types';
import { activationHeaders } from 'services/ActivationTokenService';

const BASE_URL = '/v1/companies';

export const getCompanies = async (
  page: number,
  perPage: number,
  searchBy: string
): Promise<any> => {
  const url = `${BASE_URL}`;
  const queryParamsObj = {
    page,
    per_page: perPage,
    search_term: searchBy,
  };
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiClient.get<any>(fullUrl);

  return data.data;
};

export const getCompany = async (companyId: number): Promise<any> => {
  const url = `${BASE_URL}/${companyId}`;
  const { data } = await apiClient.get<any>(url);
  return data.data;
};

// Company management
export const updateCompany = async (
  companyId: number,
  editCompanyDTO: Partial<IEditCompanyDataDTO>
): Promise<any> => {
  const url = `${BASE_URL}/${companyId}`;
  const { data } = await apiClient.patch<any>(url, editCompanyDTO);

  return data.data;
};

export const uploadCompanyLogo = async (
  companyId: number,
  formData: FormData
) => {
  const headers = { 'Content-Type': 'multipart/form-data' };
  const url = `${BASE_URL}/${companyId}/logo`;
  const { data } = await apiClient.post(url, formData, { headers });

  return data.data;
};

export const deleteCompanyLogo = async (companyId: number) => {
  const url = `${BASE_URL}/${companyId}/logo`;
  const { data } = await apiClient.delete(url);

  return data.data;
};

// Create custom model

export const createCustomModel = async (companyId: number): Promise<any> => {
  const url = `${BASE_URL}/${companyId}/models?empty=True`;
  const { data } = await apiClient.post<any>(url);
  return data.data;
};

// Create new order - Supplier

export const getCompanyBuyers = async (
  page: number,
  perPage: number,
  searchBy: string,
  sortBy?: SortByOption,
  sortDirection?: SortDirection
): Promise<any> => {
  const url = `${BASE_URL}/buyers`;
  const queryParamsObj = {
    page,
    per_page: perPage,
    search_term: searchBy,
    ...(sortBy && { sort_by: sortBy }),
    ...(sortDirection && { sort_direction: sortDirection }),
  };
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiClient.get<any>(fullUrl);

  return data.data;
};

export const getCompanyShippingAddresses = async (
  companyId: number,
  appMode: AppMode
): Promise<any> => {
  const url = `v1/shipping-addresses`;

  const { data } = await apiClient.get<any>(url, {
    params: {
      ...(appMode === AppMode.SUPPLIER && { company_buyer_id: companyId }),
      ...(appMode === AppMode.BUYER && { company_seller_id: companyId }),
    },
  });

  return data.data;
};

export const getDeliveryDays = async (
  shippingType: ShippingType,
  shoppingCartId: string
): Promise<any> => {
  const url = `/v1/delivery-days`;
  const includeBuyerCalendar =
    shippingType === ShippingType.DEFAULT ||
    shippingType === ShippingType.PREDEFINED;
  const { data } = await apiClient.get<any>(url, {
    params: {
      include_buyer_calendar: includeBuyerCalendar,
      cart_id: shoppingCartId,
    },
  });

  return data.data;
};

export const deleteBuyer = async (buyerId: number): Promise<any> => {
  const url = `${BASE_URL}/buyers/${buyerId}`;
  const { data } = await apiClient.delete<any>(url);
  return data.data;
};

export const getSingleBuyer = async (id: number): Promise<any> => {
  const url = `${BASE_URL}/buyers/${id}`;
  const { data } = await apiClient.get<any>(url);
  return data.data;
};

export const getEligibleBuyers = async (
  page: number,
  perPage: number,
  searchBy: string
): Promise<any> => {
  const url = `${BASE_URL}/buyers/eligible`;
  const queryParamsObj = {
    page: page,
    per_page: perPage,
    search_term: searchBy,
  };
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiClient.get<any>(fullUrl);

  return data.data;
};

export const addBuyer = async (
  companyId: number,
  reference: string
): Promise<any> => {
  const url = `${BASE_URL}/buyers`;
  const payload = {
    company_id: companyId,
    customer_number: reference,
  };
  const { data } = await apiClient.post<any>(url, payload);
  return data.data;
};

export const editBuyer = async (dto: IEditBuyerDTO): Promise<any> => {
  const url = `${BASE_URL}/buyers/${dto.id}`;
  const { data } = await apiClient.patch<any>(url, {
    is_active: dto.isActive,
  });
  return data.data;
};

export const getCompaniesForModel = async (
  page: number,
  perPage: number,
  searchBy: string,
  modelId: number
): Promise<any> => {
  const queryParamsObj = {
    page: page,
    per_page: perPage,
    search_term: searchBy,
  };
  const url = `${BASE_URL}/models/${modelId}/buyers`;
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiClient.get<any>(fullUrl);
  return data.data;
};

export const getMyModels = async (
  page: number,
  perPage: number,
  searchBy: string,
  categoryId: number | null
): Promise<any> => {
  const url = `${BASE_URL}/models`;
  const queryParamsObj = {
    page: page,
    per_page: perPage,
    search_term: searchBy,
    category_id: categoryId,
  };
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiClient.get<any>(fullUrl);

  return data.data;
};

export const getMyModelById = async (modelId: number): Promise<any> => {
  const url = `${BASE_URL}/models/${modelId}`;
  const { data } = await apiClient.get<any>(url);

  return data.data;
};

export const togglePublicModel = async (
  dto: ITogglePublicModelDTO
): Promise<any> => {
  const url = `${BASE_URL}/models/${dto.modelId}`;
  const { data } = await apiClient.patch<any>(url, {
    is_public: dto.is_public,
  });
  return data.data;
};

// Users
export const getUsers = async (
  page: number,
  perPage: number,
  searchBy: string
): Promise<any> => {
  const url = `${BASE_URL}/users`;
  const queryParamsObj = {
    page,
    per_page: perPage,
    search_term: searchBy,
  };
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiClient.get<any>(fullUrl);

  return data.data;
};

export const addCompanyUser = async (email: string): Promise<any> => {
  const url = `${BASE_URL}/users`;
  const { data } = await apiClient.post<any>(url, {
    email,
  });
  return data;
};

export const deleteCompanyUser = async (userId: number): Promise<any> => {
  const url = `${BASE_URL}/users/${userId}`;
  const { data } = await apiClient.delete<any>(url);

  return data.data;
};

// Buyer new order
export const getCompanySuppliers = async (
  page: number,
  perPage: number,
  searchBy: string,
  sortBy?: SortByOption,
  sortDirection?: SortDirection
): Promise<any> => {
  const url = `${BASE_URL}/sellers`;
  const queryParamsObj = {
    page,
    per_page: perPage,
    search_term: searchBy,
    ...(sortBy && { sort_by: sortBy }),
    ...(sortDirection && { sort_direction: sortDirection }),
  };
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiClient.get<any>(fullUrl);

  return data.data;
};

// A company activation link's later steps. They act as the user who signed in,
// or up, on the link: core reads it from the activation token, sent as the
// bearer token, and ignores user_id (still sent, for a core from before the
// activation token). Not apiClient: the browser's own login has no part in it,
// and its 401 handling would sign that login out.
export const registerNewCompany = async (
  params: IRegisterNewCompanyParams
): Promise<any> => {
  const { token, userId, name, email, address } = params;
  const url = `${BASE_URL}/register`;
  const payload = {
    token,
    user_id: userId,
    name,
    email,
    address,
  };
  const { data } = await apiAuthClient.post<any>(url, payload, {
    headers: activationHeaders(),
  });
  return data.data;
};

export const getCompaniesEligible = async (
  userId: string,
  token: string,
  searchBy: string
): Promise<any> => {
  const url = `${BASE_URL}/eligible`;
  const queryParamsObj = {
    user_id: userId,
    token: token,
    search_term: searchBy,
  };
  const fullUrl = getUrlWithQueryParams(url, queryParamsObj);
  const { data } = await apiAuthClient.get<any>(fullUrl, {
    headers: activationHeaders(),
  });

  return data.data;
};

export const selectCompanyLogin = async (
  token: string,
  userId: string,
  companyId: number
): Promise<any> => {
  const url = `${BASE_URL}/merge`;
  const payload = {
    token,
    user_id: userId,
    company_id: companyId,
  };
  const { data } = await apiAuthClient.post<any>(url, payload, {
    headers: activationHeaders(),
  });
  return data.data;
};
